import { Fail, Ok } from './value';
/**
 * The “Result Pattern” is an alternative to exception-based error handling. Instead of triggering exceptions, a special result object is returned that contains the success or failure of an operation as well as error information. This allows the normal program flow to run more smoothly.
 */
export class Result {
    #value;
    constructor(inner) {
        this.#value = inner;
    }
    get isOk() {
        return this.#value.ok;
    }
    get isErr() {
        return !this.#value.ok;
    }
    get error() {
        return this.#value.error;
    }
    /**
     * Return value or throw error
     */
    unwrap() {
        return this.#value.unwrap();
    }
    /**
     * Return value or fallback
     * @param fallback
     */
    unwrapOr(fallback) {
        return this.#value.unwrapOr(fallback);
    }
    /**
     * Return value or call passed function with error as argument
     * @param fn
     */
    unwrapOrElse(fn) {
        return this.#value.unwrapOrElse(fn);
    }
    /**
     * Return value or throw passed error
     * @param err Error
     */
    expect(err) {
        return this.#value.expect(err);
    }
    match(cases) {
        return this.#value.match(cases);
    }
    /**
     * Apply function to success value
     */
    map(fn) {
        if (this.isErr) {
            return Result.Err(this.error);
        }
        return Result.Ok(fn(this.unwrap()));
    }
    flatMap(fn) {
        return fn(this.unwrap());
    }
    async mapAsync(fn) {
        if (this.isErr) {
            return Result.Err(this.error);
        }
        return Result.Ok(await fn(this.unwrap()));
    }
    flatMapAsync(fn) {
        return fn(this.unwrap());
    }
    tap(fn) {
        this.#value.match({ ok: fn, err: () => undefined });
        return this;
    }
    tapError(fn) {
        this.#value.match({ ok: () => undefined, err: fn });
        return this;
    }
    mapError(fn) {
        if (this.isOk) {
            return this;
        }
        return Result.Err(fn(this.error));
    }
    orElse(fn) {
        if (this.isOk) {
            return this;
        }
        return fn(this.error);
    }
    static Ok(...args) {
        return new Result(new Ok(args[0]));
    }
    static Err(error) {
        return new Result(new Fail(error));
    }
    static combine(results) {
        for (const result of results) {
            if (result.isErr) {
                return result;
            }
        }
        return Result.Ok();
    }
    static fromCatch(fn, mapError) {
        try {
            return Result.Ok(fn());
        }
        catch (err) {
            return Result.Err(mapError ? mapError(err) : err);
        }
    }
    static async fromAsync(fn, mapError) {
        try {
            return Result.Ok(await fn());
        }
        catch (err) {
            return Result.Err(mapError ? mapError(err) : err);
        }
    }
}
//# sourceMappingURL=result.js.map